// === EFFETS DE PARTICULES ===
// Système d'effets visuels minimaliste pour le combat.
// Utilise le système natif de Phaser (zéro dépendance externe).
//
// Effets disponibles :
//   - blood(x, y)          : impact mêlée/tir, particules rouge/orange, ~500ms
//   - explosion(x, y)      : Frappe Sauvage / mort bomber, particules orange/jaune avec glow ADD, ~600ms
//   - dust(x, y)           : mort ennemi, poussière grise + flash blanc, ~400ms
//
// Performance : chaque appel crée un emitter one-shot qui se détruit après son lifespan.
// Le toggle `ParticleSettings.reduced` divise la quantité par 2.5 pour les devices faibles.

import Phaser from 'phaser';

const TEXTURE_KEY = 'particle_dot';
// Texture suffisamment grande pour être visible à côté des sprites de 50-64px de la grille.
const TEXTURE_SIZE = 12;

/** Singleton paramètre : effets visuels réduits (mobile/devices faibles). */
class _ParticleSettings {
  private static STORAGE_KEY = 'deadtide_reduced_effects';
  private reduced: boolean;

  constructor() {
    this.reduced = this.load();
  }

  isReduced(): boolean {
    return this.reduced;
  }

  setReduced(value: boolean): void {
    this.reduced = value;
    this.save();
  }

  toggle(): boolean {
    this.setReduced(!this.reduced);
    return this.reduced;
  }

  private load(): boolean {
    try {
      return localStorage.getItem(_ParticleSettings.STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private save(): void {
    try {
      localStorage.setItem(_ParticleSettings.STORAGE_KEY, this.reduced ? '1' : '0');
    } catch { /* localStorage indisponible */ }
  }
}

export const ParticleSettings = new _ParticleSettings();

/**
 * Génère la texture circulaire blanche utilisée pour toutes les particules.
 * Idempotent — vérifie l'existence dans le registre Phaser avant de créer.
 * Utilise `add.graphics` puis destroy (plus fiable que `make.graphics(false)`
 * sur certaines versions de Phaser 3.80+).
 */
export function ensureParticleTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEXTURE_KEY)) return;
  const g = scene.add.graphics({ x: 0, y: 0 });
  g.fillStyle(0xffffff, 1);
  g.fillCircle(TEXTURE_SIZE / 2, TEXTURE_SIZE / 2, TEXTURE_SIZE / 2);
  g.generateTexture(TEXTURE_KEY, TEXTURE_SIZE, TEXTURE_SIZE);
  g.destroy();
}

/** Quantité ajustée selon le réglage "effets réduits". */
function quantity(base: number): number {
  return ParticleSettings.isReduced() ? Math.max(1, Math.floor(base * 0.4)) : base;
}

/**
 * Sang : burst rouge/orange, idéal pour les impacts mêlée/tir.
 * 8 particules par défaut, ~500ms. Pas de blendMode (sang opaque).
 */
export function blood(scene: Phaser.Scene, x: number, y: number): void {
  ensureParticleTexture(scene);
  const emitter = scene.add.particles(0, 0, TEXTURE_KEY, {
    speed: { min: 80, max: 180 },
    angle: { min: 0, max: 360 },
    scale: { start: 1.4, end: 0.1 },
    alpha: { start: 1, end: 0 },
    lifespan: { min: 350, max: 500 },
    tint: [0xc0392b, 0xe74c3c, 0xf39c12],
    emitting: false,
  });
  emitter.setDepth(20);
  emitter.explode(quantity(8), x, y);
  scene.time.delayedCall(550, () => emitter.destroy());
}

/**
 * Explosion : burst large orange/jaune avec blendMode ADD (effet glow).
 * 14 particules par défaut, ~600ms. Pour Frappe Sauvage, mort bomber, AoE.
 */
export function explosion(scene: Phaser.Scene, x: number, y: number): void {
  ensureParticleTexture(scene);
  const emitter = scene.add.particles(0, 0, TEXTURE_KEY, {
    speed: { min: 120, max: 280 },
    angle: { min: 0, max: 360 },
    scale: { start: 2.0, end: 0.1 },
    alpha: { start: 1, end: 0 },
    lifespan: { min: 400, max: 600 },
    tint: [0xe67e22, 0xf39c12, 0xf1c40f, 0xe74c3c],
    blendMode: 'ADD',
    emitting: false,
  });
  emitter.setDepth(20);
  emitter.explode(quantity(14), x, y);
  scene.time.delayedCall(650, () => emitter.destroy());
}

/**
 * Poussière + flash : mort d'ennemi. Jouée juste avant l'animation de mort.
 * 10 particules grises + 1 flash blanc rond.
 */
export function dust(scene: Phaser.Scene, x: number, y: number): void {
  ensureParticleTexture(scene);
  const emitter = scene.add.particles(0, 0, TEXTURE_KEY, {
    speed: { min: 50, max: 130 },
    angle: { min: 200, max: 340 },  // vers le haut
    scale: { start: 1.2, end: 0.1 },
    alpha: { start: 0.8, end: 0 },
    lifespan: { min: 300, max: 450 },
    tint: [0x95a5a6, 0xbdc3c7, 0x7f8c8d],
    emitting: false,
  });
  emitter.setDepth(20);
  emitter.explode(quantity(10), x, y);
  scene.time.delayedCall(500, () => emitter.destroy());

  // Flash blanc bref (skip en mode réduit pour économiser)
  if (!ParticleSettings.isReduced()) {
    const flash = scene.add.circle(x, y, 18, 0xffffff, 0.85).setDepth(21);
    scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 2.2,
      duration: 250,
      ease: 'Cubic.Out',
      onComplete: () => flash.destroy(),
    });
  }
}
